/** Static utility functions (note that there is another such tool in Hekate) */
class ExternalUtil {
    /** Get the origin (aka domain name) of this instance */
    static getSafeOrigin() {
        // We can use `window.top!.` because window is always a window when in the browser
        if (typeof self === 'object' && typeof self.origin === 'string') {
            return self.origin;
        }
        return window.top.location.toString();
    }
    /** True if this is running on a production domain */
    static isProduction() {
        return ExternalUtil.getSafeOrigin() === 'https://www.onshape.com';
    }
    /** True if this is running on stage */
    static isStage() {
        return ExternalUtil.getSafeOrigin() === 'https://www-stage.dev.onshape.com';
    }
    /** Get page version token typically for clearing cache */
    static getPageVersion() {
        const elm = document.querySelector("[name='page-version']");
        if (!elm) {
            return Date.now().toString().substring(0, 8);
        }
        return elm.getAttribute('value');
    }
    /** Create a script tag and append to the bottom of the page */
    static createScriptTag({ src, isAsync, className, }) {
        const scriptTag = document.createElement('script');
        scriptTag.async = isAsync;
        scriptTag.src = src;
        scriptTag.className = className;
        document.body.appendChild(scriptTag);
    }
    /** return the two letter site language */
    static getLanguage() {
        var _a;
        // try to get lang from meta tag
        const metaLang = (_a = document
            .querySelector('meta[http-equiv=content-language]')
            .getAttribute('content')) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
        if (metaLang && ExternalUtil.languages.includes(metaLang)) {
            return metaLang;
        }
        // try to get lang from the url
        let lang = 'en';
        const pathBits = window.location.pathname.split('/');
        pathBits.shift();
        const firstBit = pathBits.shift();
        if (firstBit) {
            const possibleSiteLanguage = firstBit.toLocaleLowerCase();
            lang = ExternalUtil.languages.includes(possibleSiteLanguage) ? possibleSiteLanguage : 'en';
        }
        return lang;
    }
    /** Find the display field and get its value or return a default value */
    static findValueInHekateFormSubmitOrDefault({ formDefinition, fieldName, defaultVal, }) {
        const found = formDefinition.displayFields.find((field) => field.htmlName === fieldName);
        if (!found) {
            return defaultVal;
        }
        return found.val || defaultVal;
    }
    /** Return true if the two objects are equal (note this is single depth) */
    static isObjectsEqual(lhs, rhs) {
        const lk = Object.keys(lhs);
        const rk = Object.keys(rhs);
        // check that there are the same exact keys
        if (lk.length !== rk.length) {
            return false;
        }
        // check if there are any non-equal keys
        return lk.filter((kk) => lhs[kk] !== rhs[kk]).length === 0;
    }
    /** parsing string into HTML Node */
    static stringToHTML(text) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const container = document.createElement('div');
        Array.from(doc.body.children).forEach((node) => {
            const tagName = node.tagName.toLowerCase();
            if (tagName !== 'script' && tagName !== 'style') {
                container.appendChild(node);
            }
        });
        Array.from(doc.getElementsByTagName('script')).forEach((node) => {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            if (node.src !== '') {
                scriptTag.src = node.src;
            }
            else {
                scriptTag.innerHTML = node.innerHTML;
            }
            container.appendChild(scriptTag);
        });
        Array.from(doc.getElementsByTagName('style')).forEach((node) => container.appendChild(node));
        return container;
    }
    /** Build a local resource URL from a pathname - uses environment protocol and host */
    static buildUrlFromLocation({ pathname }) {
        const { protocol, host } = document.location;
        const url = new URL(`${protocol}//${host}`);
        url.pathname = pathname;
        return url;
    }
    static safeParseIntOrDefault({ string, defaultVal, }) {
        try {
            return parseInt(`${string}`.trim(), 10);
        }
        catch (e) {
            console.warn('Error parsing int', e);
            return defaultVal;
        }
    }
    /** Handle some special chars that are not handled by decode URI */
    static cleanSpecialChars(text) {
        return text
            .replace(/(<([^>]+)>)/gi, ' ')
            .replace(/&apos;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
    }
}
/** All the languages we support on the site */
ExternalUtil.languages = [
    'cn',
    'de',
    'en',
    'es',
    'fr',
    'it',
    'ja',
    'ko',
    'pt',
    'ru',
    'tw',
];
export default ExternalUtil;
